import { zodResolver } from '@hookform/resolvers/zod'
import { Edit, ListPlus, Plus, RefreshCw, Settings2, Trash2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { z } from 'zod'

import type {
  ExpenseConcept,
  ExpenseConceptPayload,
  ExpenseItem,
  ExpensePayload,
} from '../../../services/expense.service'
import { FormError } from '../../ui/FormError'
import { Button } from '../../ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog'
import { DiscreteLoader } from '../../ui/discrete-loader'
import { Input } from '../../ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../ui/select'

const expenseFormSchema = z.object({
  concept_id: z.string().min(1, 'Selecciona un concepto'),
  expense_date: z.string().min(1, 'La fecha es obligatoria'),
  amount: z
    .string()
    .trim()
    .min(1, 'La cantidad es obligatoria')
    .regex(/^\d+(?:\.\d+)?$/, 'Ingresa una cantidad decimal válida')
    .refine((value) => Number(value) > 0, 'La cantidad debe ser mayor a cero'),
})

type ExpenseFormValues = z.infer<typeof expenseFormSchema>

interface ExpenseFormModalProps {
  open: boolean
  formVersion: number
  expense: ExpenseItem | null
  concepts: ExpenseConcept[]
  conceptsLoading: boolean
  onOpenChange: (open: boolean) => void
  onOpenConcepts: () => void
  onSubmit: (payload: ExpensePayload) => Promise<void>
}

function getToday() {
  const today = new Date()
  const offset = today.getTimezoneOffset() * 60_000

  return new Date(today.getTime() - offset).toISOString().slice(0, 10)
}

export function ExpenseFormModal({
  open,
  formVersion,
  expense,
  concepts,
  conceptsLoading,
  onOpenChange,
  onOpenConcepts,
  onSubmit,
}: ExpenseFormModalProps) {
  const form = useForm<ExpenseFormValues>({
    resolver: zodResolver(expenseFormSchema),
    defaultValues: {
      concept_id: '',
      expense_date: getToday(),
      amount: '',
    },
  })

  useEffect(() => {
    form.reset({
      concept_id: expense ? String(expense.concept_id) : '',
      expense_date: expense?.expense_date.slice(0, 10) ?? getToday(),
      amount: expense ? String(expense.amount) : '',
    })
  }, [expense, form, formVersion])

  const submit = (values: ExpenseFormValues) => onSubmit({
    concept_id: Number(values.concept_id),
    expense_date: values.expense_date,
    amount: Number(values.amount),
  })

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{expense ? 'Editar gasto' : 'Nuevo gasto'}</DialogTitle>
          <DialogDescription>
            Captura el concepto, la fecha y la cantidad del gasto.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={form.handleSubmit(submit)} className="space-y-4 py-2">
          <div>
            <label htmlFor="expense-concept" className="mb-1 block text-sm font-medium text-card-foreground">
              Concepto <span className="text-red-500">*</span>
            </label>
            <div className="flex items-start gap-2">
              <div className="min-w-0 flex-1">
                <Controller
                  control={form.control}
                  name="concept_id"
                  render={({ field }) => (
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={conceptsLoading || concepts.length === 0}
                    >
                      <SelectTrigger
                        id="expense-concept"
                        aria-label="Concepto"
                        aria-invalid={Boolean(form.formState.errors.concept_id)}
                      >
                        <SelectValue
                          placeholder={conceptsLoading
                            ? 'Cargando conceptos...'
                            : concepts.length === 0
                              ? 'No hay conceptos disponibles'
                              : 'Selecciona un concepto'}
                        />
                      </SelectTrigger>
                      <SelectContent>
                        {concepts.map((concept) => (
                          <SelectItem key={concept.id} value={String(concept.id)}>
                            {concept.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                />
                <FormError message={form.formState.errors.concept_id?.message} />
              </div>
              <Button
                type="button"
                variant="outline"
                title="Gestionar conceptos"
                aria-label="Gestionar conceptos"
                onClick={onOpenConcepts}
                className="shrink-0"
              >
                <Settings2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Conceptos</span>
              </Button>
            </div>
          </div>

          <div>
            <label htmlFor="expense-date" className="mb-1 block text-sm font-medium text-card-foreground">
              Fecha <span className="text-red-500">*</span>
            </label>
            <Input id="expense-date" type="date" {...form.register('expense_date')} />
            <FormError message={form.formState.errors.expense_date?.message} />
          </div>

          <div>
            <label htmlFor="expense-amount" className="mb-1 block text-sm font-medium text-card-foreground">
              Cantidad <span className="text-red-500">*</span>
            </label>
            <Input
              id="expense-amount"
              type="number"
              min="0"
              step="any"
              inputMode="decimal"
              placeholder="0.00"
              {...form.register('amount')}
            />
            <FormError message={form.formState.errors.amount?.message} />
          </div>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={form.formState.isSubmitting}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? <DiscreteLoader /> : 'Guardar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

const conceptFormSchema = z.object({
  name: z
    .string()
    .trim()
    .min(1, 'El nombre es obligatorio')
    .max(100, 'El nombre no puede superar 100 caracteres'),
})

type ConceptFormValues = z.infer<typeof conceptFormSchema>

interface ExpenseConceptsModalProps {
  open: boolean
  concepts: ExpenseConcept[]
  isLoading: boolean
  isError: boolean
  onOpenChange: (open: boolean) => void
  onRetry: () => void
  onCreate: (payload: ExpenseConceptPayload) => Promise<boolean>
  onUpdate: (id: number, payload: ExpenseConceptPayload) => Promise<boolean>
  onDeleteRequest: (concept: ExpenseConcept) => void
}

export function ExpenseConceptsModal({
  open,
  concepts,
  isLoading,
  isError,
  onOpenChange,
  onRetry,
  onCreate,
  onUpdate,
  onDeleteRequest,
}: ExpenseConceptsModalProps) {
  const form = useForm<ConceptFormValues>({
    resolver: zodResolver(conceptFormSchema),
    defaultValues: { name: '' },
  })
  const [editingConcept, setEditingConcept] = useState<ExpenseConcept | null>(null)

  const cancelEditing = () => {
    setEditingConcept(null)
    form.reset({ name: '' })
  }

  const startEditing = (concept: ExpenseConcept) => {
    setEditingConcept(concept)
    form.reset({ name: concept.name })
  }

  const submit = async (values: ConceptFormValues) => {
    const saved = editingConcept
      ? await onUpdate(editingConcept.id, values)
      : await onCreate(values)

    if (saved) cancelEditing()
  }

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) cancelEditing()
    onOpenChange(nextOpen)
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Gestionar conceptos</DialogTitle>
          <DialogDescription>
            Crea, edita o elimina los conceptos disponibles para clasificar gastos.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(submit)} className="flex flex-col gap-3 sm:flex-row sm:items-start">
          <div className="min-w-0 flex-1">
            <label htmlFor="concept-name" className="mb-1 block text-sm font-medium text-card-foreground">
              Nombre del concepto <span className="text-red-500">*</span>
            </label>
            <Input id="concept-name" placeholder="Ej. Servicios, papelería" {...form.register('name')} />
            <FormError message={form.formState.errors.name?.message} />
          </div>
          <div className="flex gap-2 pt-0 sm:pt-6">
            {editingConcept && (
              <Button type="button" variant="outline" onClick={cancelEditing}>
                Cancelar
              </Button>
            )}
            <Button type="submit" disabled={form.formState.isSubmitting}>
              {form.formState.isSubmitting ? (
                <DiscreteLoader />
              ) : editingConcept ? (
                'Actualizar'
              ) : (
                <><Plus className="mr-2 h-4 w-4" />Agregar</>
              )}
            </Button>
          </div>
        </form>

        <div className="max-h-72 overflow-y-auto rounded-lg border border-table-border bg-table-bg">
          {isLoading ? (
            <div className="min-h-40"><DiscreteLoader text="Cargando conceptos..." /></div>
          ) : isError ? (
            <div className="flex min-h-40 flex-col items-center justify-center p-5 text-center">
              <p className="font-medium text-table-foreground">No fue posible cargar los conceptos</p>
              <Button type="button" variant="outline" className="mt-3" onClick={onRetry}>
                <RefreshCw className="mr-2 h-4 w-4" />Reintentar
              </Button>
            </div>
          ) : concepts.length === 0 ? (
            <div className="flex min-h-40 flex-col items-center justify-center p-5 text-center">
              <ListPlus className="mb-3 h-8 w-8 text-card-description" />
              <p className="font-medium text-table-foreground">No hay conceptos registrados</p>
              <p className="mt-1 text-sm text-card-description">
                Agrega el primer concepto con el formulario superior.
              </p>
            </div>
          ) : (
            <ul className="divide-y divide-table-border">
              {concepts.map((concept) => (
                <li key={concept.id} className="flex items-center justify-between gap-3 px-4 py-3 hover:bg-table-row-hover">
                  <span className="min-w-0 truncate font-medium text-table-foreground">{concept.name}</span>
                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      aria-label={`Editar concepto ${concept.name}`}
                      title="Editar"
                      onClick={() => startEditing(concept)}
                      className="rounded p-2 text-table-foreground transition-colors hover:bg-muted"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      aria-label={`Eliminar concepto ${concept.name}`}
                      title="Eliminar"
                      onClick={() => onDeleteRequest(concept)}
                      className="rounded p-2 text-red-500 transition-colors hover:bg-red-50"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
